/**
 * Merges Predictive Search product matches into the full search results page.
 * Shopify's full-search index can lag behind predictive search for new products,
 * SKUs, and subscription items. Supplement products always come from predictive
 * search for the original query — never replaced by a separate native search.
 */
(function () {
  const SEARCH_FIELDS =
    'title,product_type,variants.title,vendor,variants.sku,variants.barcode';

  let supplementProducts = [];
  let supplementProductElements = new Map();
  let supplementClientMode = false;
  let supplementSearchQuery = '';
  let activeFacetFormId = 'FacetFiltersForm';

  function updateSearchFacetsCount(count) {
    const countText = `${count} result${count === 1 ? '' : 's'}`;
    document
      .querySelectorAll('#ProductCountDesktop, #ProductCount')
      .forEach((element) => {
        element.textContent = countText;
      });
  }

  function clearSupplementLoadingState() {
    document
      .querySelectorAll('#ProductCount, #ProductCountDesktop')
      .forEach((element) => {
        element.classList.remove('loading');
        element.closest('.product-count__text')?.classList.remove('loading');
      });

    document
      .querySelector('#ProductGridContainer .collection')
      ?.classList.remove('loading');
  }

  function rememberPriceFilterDefaults() {
    document
      .querySelectorAll('#main-search-filters .js-filter price-range')
      .forEach((priceRange) => {
        const filterGroup = priceRange.closest('.js-filter');
        const selectedElement = filterGroup?.querySelector(
          '.facets__header .facets__selected, .facets__header-vertical .facets__selected',
        );

        if (selectedElement && !selectedElement.dataset.defaultText) {
          selectedElement.dataset.defaultText =
            selectedElement.textContent.trim();
        }
      });
  }

  function resetPriceFilterUi(filterGroup) {
    const selectedElement = filterGroup?.querySelector(
      '.facets__header .facets__selected, .facets__header-vertical .facets__selected',
    );

    if (selectedElement?.dataset.defaultText) {
      selectedElement.textContent = selectedElement.dataset.defaultText;
      selectedElement.classList.remove('hidden');
    }
  }

  function clearPriceInputs(filterGroup) {
    const inputs = filterGroup
      ? filterGroup.querySelectorAll('input[name^="filter.v.price"]')
      : document.querySelectorAll(
          '#main-search-filters input[name^="filter.v.price"]',
        );

    const clearedNames = new Set();

    inputs.forEach((input) => {
      input.value = '';
      clearedNames.add(input.name);
    });

    clearedNames.forEach((name) => {
      document
        .querySelectorAll(`#main-search-filters input[name="${name}"]`)
        .forEach((input) => {
          input.value = '';
        });
    });

    const scope = filterGroup
      ? document.getElementById('main-search-filters')
      : document.getElementById('main-search-filters');
    scope?.querySelectorAll('price-range').forEach((priceRange) => {
      if (typeof priceRange.setMinAndMaxValues === 'function') {
        priceRange.setMinAndMaxValues();
      }
    });
  }

  function resetAllSupplementFacets() {
    const facetsRoot = document.getElementById('main-search-filters');
    if (!facetsRoot) return;

    getInteractiveFacetCheckboxes(facetsRoot).forEach((input) => {
      input.checked = false;
    });

    clearPriceInputs();

    facetsRoot.querySelectorAll('.js-filter').forEach((filterGroup) => {
      if (filterGroup.querySelector('price-range')) {
        resetPriceFilterUi(filterGroup);
      }
    });

    const root = document.querySelector('[data-search-results-root]');
    restoreSearchSortOptions(root);
    setSupplementFacetQuery(supplementSearchQuery);
  }

  function resetSupplementFacetGroup(filterGroup) {
    if (!filterGroup) return;

    getInteractiveFacetCheckboxes(filterGroup).forEach((input) => {
      input.checked = false;
    });

    if (filterGroup.querySelector('price-range')) {
      clearPriceInputs(filterGroup);
      document
        .querySelectorAll('#main-search-filters .js-filter price-range')
        .forEach((priceRange) => {
          resetPriceFilterUi(priceRange.closest('.js-filter'));
        });
    }
  }

  function syncMatchingPriceInputs(sourceInput) {
    if (!sourceInput.name?.startsWith('filter.v.price')) return;

    document
      .querySelectorAll(
        `#main-search-filters input[name="${sourceInput.name}"]`,
      )
      .forEach((input) => {
        if (input !== sourceInput) {
          input.value = sourceInput.value;
        }
      });
  }

  function isInteractiveFacetCheckbox(input) {
    return (
      input?.type === 'checkbox' &&
      input.name?.startsWith('filter.') &&
      !input.closest('.no-js-list')
    );
  }

  function getInteractiveFacetCheckboxes(container) {
    if (!container) return [];

    return [...container.querySelectorAll('input[type="checkbox"][name^="filter."]')].filter(
      isInteractiveFacetCheckbox,
    );
  }

  function syncMatchingFacetCheckboxes(sourceInput, checked) {
    if (!isInteractiveFacetCheckbox(sourceInput)) return;

    document
      .querySelectorAll(
        `input[type="checkbox"][name="${sourceInput.name}"][value="${sourceInput.value}"]`,
      )
      .forEach((input) => {
        if (isInteractiveFacetCheckbox(input)) {
          input.checked = checked;
        }
      });
  }

  function formatFiltersSelected(count) {
    return count === 1 ? '1 selected' : `${count} selected`;
  }

  function setSupplementFacetQuery(query) {
    if (!query) return;

    document
      .querySelectorAll('#main-search-filters form')
      .forEach((form) => {
        let input = form.querySelector('input[name="q"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'q';
          form.prepend(input);

          const prefix = document.createElement('input');
          prefix.type = 'hidden';
          prefix.name = 'options[prefix]';
          prefix.value = 'none';
          form.prepend(prefix);
        }

        input.value = query;
      });
  }

  function bindMenuDrawers(container) {
    container?.querySelectorAll('menu-drawer').forEach((drawer) => {
      if (typeof drawer.bindEvents === 'function') {
        drawer.bindEvents();
      }
    });
  }

  function getVisibleProductCount() {
    const grid = document.querySelector('#product-grid ul.product-grid');
    if (!grid) return 0;

    return [...grid.querySelectorAll('[data-product-handle]')].filter(
      (element) => {
        const item = element.closest('.grid__item') || element;
        return item.hidden !== true && item.style.display !== 'none';
      },
    ).length;
  }

  async function fetchProduct(handle) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(
        `${root}products/${encodeURIComponent(handle)}.js`,
      );
      return response.ok ? response.json() : null;
    } catch {
      return null;
    }
  }

  function productMatchesSearchQuery(product, query) {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return true;

    if (
      product.variants?.some(
        (variant) => variant.sku?.toLowerCase() === normalized,
      )
    ) {
      return true;
    }

    if (
      product.variants?.some(
        (variant) => variant.barcode?.toLowerCase() === normalized,
      )
    ) {
      return true;
    }

    return false;
  }

  function restoreSearchSortOptions(root) {
    const optionsJson = root.dataset.searchSortOptions;
    if (!optionsJson) return;

    let options;
    try {
      options = JSON.parse(optionsJson);
    } catch {
      return;
    }

    if (!options.length) return;

    const defaultSort =
      root.dataset.searchDefaultSort || options[0].value;

    document
      .querySelectorAll('#main-search-filters select[name="sort_by"]')
      .forEach((select) => {
        select.innerHTML = options
          .map(
            (option) =>
              `<option value="${option.value.replace(/"/g, '&quot;')}"${
                option.value === defaultSort ? ' selected' : ''
              }>${option.name}</option>`,
          )
          .join('');
      });
  }

  function syncSortSelects(value) {
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      select.value = value;
    });
  }

  async function loadCollectionFacetsShell(facetsAside, searchQuery, root) {
    const facetsUrl = facetsAside.dataset.searchFacetsUrl;
    if (!facetsUrl) return false;

    try {
      const response = await fetch(facetsUrl);
      if (!response.ok) return false;

      const doc = new DOMParser().parseFromString(
        await response.text(),
        'text/html',
      );
      const content =
        doc.querySelector('#main-collection-filters') ||
        doc.querySelector('.facets-wrapper');

      if (!content?.querySelector('.js-filter')) return false;

      facetsAside.innerHTML = content.innerHTML;
      setSupplementFacetQuery(searchQuery);
      restoreSearchSortOptions(root);
      rememberPriceFilterDefaults();
      bindMenuDrawers(facetsAside);
      return true;
    } catch {
      return false;
    }
  }

  function getFacetForms() {
    const facetsRoot = document.getElementById('main-search-filters');
    if (!facetsRoot) return [];

    return [
      document.getElementById('FacetFiltersForm'),
      document.getElementById('FacetFiltersFormMobile'),
      document.getElementById('FacetSortForm'),
      ...facetsRoot.querySelectorAll('form'),
    ].filter((form, index, forms) => form && forms.indexOf(form) === index);
  }

  function getFacetForm() {
    return (
      document.getElementById(activeFacetFormId) ||
      document.getElementById('FacetFiltersForm') ||
      getFacetForms()[0]
    );
  }

  function getFacetFormParams() {
    const params = new URLSearchParams();
    const facetsRoot = document.getElementById('main-search-filters');
    const forms = getFacetForms();

    forms.forEach((form) => {
      getInteractiveFacetCheckboxes(form)
        .filter((input) => input.checked)
        .forEach((input) => {
          params.append(input.name, input.value);
        });
    });

    (facetsRoot || document)
      .querySelectorAll('input[name^="filter.v.price"]')
      .forEach((input) => {
        if (input.value) {
          params.set(input.name, input.value);
        }
      });

    const sortSelect =
      facetsRoot?.querySelector('select[name="sort_by"]') ||
      document.querySelector('#main-search-filters select[name="sort_by"]');
    if (sortSelect?.value) {
      params.set('sort_by', sortSelect.value);
    }

    const queryForm = forms.find((form) => form.querySelector('input[name="q"]'));
    const queryInput = queryForm?.querySelector('input[name="q"]');
    if (queryInput?.value) {
      params.set('q', queryInput.value);
    }

    const prefixInput = queryForm?.querySelector('input[name="options[prefix]"]');
    if (prefixInput?.value) {
      params.set('options[prefix]', prefixInput.value);
    }

    return params;
  }

  function hasActiveFacetFilters(params = getFacetFormParams()) {
    for (const [key, value] of params.entries()) {
      if (key === 'q' || key === 'sort_by' || key.startsWith('options')) {
        continue;
      }

      if (value) return true;
    }

    return false;
  }

  function ensureSupplementProductsInGrid() {
    const grid = document.querySelector('#product-grid ul.product-grid');
    if (!grid || supplementProductElements.size === 0) return;

    supplementProductElements.forEach((template, handle) => {
      if (!grid.querySelector(`[data-product-handle="${handle}"]`)) {
        grid.appendChild(template.cloneNode(true));
      }
    });
  }

  function cacheSupplementProductElement(handle, element) {
    supplementProductElements.set(handle, element.cloneNode(true));
  }

  function productMatchesAvailability(product, value) {
    const inStock =
      product.available === true ||
      product.variants?.some((variant) => variant.available);
    const normalized = String(value).toLowerCase();
    const wantsInStock =
      normalized === '1' ||
      normalized === 'true' ||
      normalized === 'in_stock' ||
      normalized === 'in stock';
    const wantsOutStock =
      normalized === '0' ||
      normalized === 'false' ||
      normalized === 'out_of_stock' ||
      normalized === 'out of stock';

    if (wantsInStock) return inStock;
    if (wantsOutStock) return !inStock;
    return true;
  }

  function productMatchesPrice(product, params) {
    const min = params.get('filter.v.price.gte');
    const max = params.get('filter.v.price.lte');
    const price = product.price;

    if (min && price < parseFloat(min) * 100) return false;
    if (max && price > parseFloat(max) * 100) return false;

    return true;
  }

  function productMatchesParams(product, params) {
    const grouped = {};

    for (const [key, value] of params.entries()) {
      if (key === 'q' || key === 'sort_by' || key.startsWith('options')) {
        continue;
      }

      if (!grouped[key]) {
        grouped[key] = [];
      }

      grouped[key].push(value);
    }

    if (grouped['filter.v.price.gte'] || grouped['filter.v.price.lte']) {
      if (!productMatchesPrice(product, params)) {
        return false;
      }
    }

    for (const [key, values] of Object.entries(grouped)) {
      if (key.startsWith('filter.v.price')) {
        continue;
      }

      if (key === 'filter.v.availability') {
        if (
          !values.some((value) => productMatchesAvailability(product, value))
        ) {
          return false;
        }
        continue;
      }

      if (key === 'filter.p.product_type') {
        if (
          !values.some(
            (value) =>
              product.type === decodeURIComponent(value).replace(/\+/g, ' '),
          )
        ) {
          return false;
        }
        continue;
      }

      if (key === 'filter.p.vendor') {
        if (
          !values.some(
            (value) =>
              product.vendor === decodeURIComponent(value).replace(/\+/g, ' '),
          )
        ) {
          return false;
        }
        continue;
      }

      if (key.startsWith('filter.p.tag')) {
        const tags = values.map((value) =>
          decodeURIComponent(value).replace(/\+/g, ' '),
        );
        if (!tags.some((tag) => product.tags?.includes(tag))) {
          return false;
        }
      }
    }

    return true;
  }

  function applyClientSort(sortBy) {
    const grid = document.querySelector('#product-grid ul.product-grid');
    if (!grid || !sortBy) return;

    const items = [...grid.querySelectorAll('.grid__item')];

    items.sort((itemA, itemB) => {
      const handleA = itemA.querySelector('[data-product-handle]')?.dataset
        .productHandle;
      const handleB = itemB.querySelector('[data-product-handle]')?.dataset
        .productHandle;
      const productA = supplementProducts.find((p) => p.handle === handleA);
      const productB = supplementProducts.find((p) => p.handle === handleB);

      if (!productA || !productB) return 0;

      switch (sortBy) {
        case 'price-ascending':
          return productA.price - productB.price;
        case 'price-descending':
          return productB.price - productA.price;
        case 'title-ascending':
          return productA.title.localeCompare(productB.title);
        case 'title-descending':
          return productB.title.localeCompare(productA.title);
        default:
          return 0;
      }
    });

    items.forEach((item) => grid.appendChild(item));
  }

  function getFilterLabel(filterGroup) {
    const summaryLabel = filterGroup.querySelector(
      '.facets__summary > div > span, .mobile-facets__summary span',
    );
    if (!summaryLabel) return '';

    return summaryLabel.textContent.replace(/\(\d+\)/, '').trim();
  }

  function getCheckboxValueLabel(input) {
    const visibleLabel = input
      .closest('label')
      ?.querySelector('[aria-hidden="true"]');

    if (visibleLabel) {
      return visibleLabel.textContent.replace(/\s*\([\d,]+\)\s*$/, '').trim();
    }

    return input.value;
  }

  function syncFacetSelectionUi() {
    const facetsRoot = document.getElementById('main-search-filters');
    if (!facetsRoot) return;

    const desktopForm = document.getElementById('FacetFiltersForm');

    facetsRoot.querySelectorAll('.js-filter').forEach((filterGroup) => {
      if (filterGroup.querySelector('price-range')) return;

      const checked = getInteractiveFacetCheckboxes(filterGroup).filter(
        (input) => input.checked,
      );
      const count = checked.length;

      filterGroup.querySelectorAll('.facets__header .facets__selected').forEach(
        (element) => {
          element.textContent = formatFiltersSelected(count);
          element.classList.toggle('hidden', count === 0);
        },
      );

      const summaryCount = filterGroup.querySelector(
        '.facets__summary .facets__selected',
      );
      if (summaryCount) {
        summaryCount.textContent = `(${count})`;
        summaryCount.classList.toggle('hidden', count === 0);
      }
    });

    if (!desktopForm) return;

    const checkedInputs = getInteractiveFacetCheckboxes(desktopForm).filter(
      (input) => input.checked,
    );

    const pillContainers = [
      facetsRoot.querySelector('#FacetFiltersForm .active-facets-desktop'),
      facetsRoot.querySelector('.active-facets-mobile'),
    ].filter(Boolean);

    const pillsHtml = checkedInputs
      .map((input) => {
        const filterGroup = input.closest('.js-filter');
        const filterLabel = getFilterLabel(filterGroup);
        const valueLabel = getCheckboxValueLabel(input);

        return `<facet-remove>
          <a href="#" class="active-facets__button active-facets__button--light" data-supplement-facet-remove data-filter-name="${input.name}" data-filter-value="${input.value}">
            <span class="active-facets__button-inner button button--tertiary">
              ${filterLabel}: ${valueLabel}
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close-small" viewBox="0 0 12 13"><path d="M11.316 1.923a.625.625 0 11-.884-.884L6 5.47 1.568 1.039a.625.625 0 10-.884.884L5.116 6.354l-4.432 4.432a.625.625 0 00.884.884L6 7.237l4.432 4.433a.625.625 0 00.884-.884L6.884 6.354l4.432-4.431z" fill="currentColor"/></svg>
              <span class="visually-hidden">Clear</span>
            </span>
          </a>
        </facet-remove>`;
      })
      .join('');

    pillContainers.forEach((container) => {
      if (checkedInputs.length === 0) {
        container.innerHTML = '';
        return;
      }

      container.innerHTML =
        pillsHtml +
        `<facet-remove class="active-facets__button-wrapper">
            <a href="#" class="active-facets__button-remove underlined-link">
              <span>Clear all</span>
            </a>
          </facet-remove>`;
    });
  }

  function countProductsForFacetOption(input) {
    const params = getFacetFormParams();

    params.delete(input.name);
    params.append(input.name, input.value);

    return supplementProducts.filter((product) =>
      productMatchesParams(product, params),
    ).length;
  }

  function updateFacetOptionLabel(input, count) {
    const label = input.closest('label');
    if (!label) return;

    label.querySelectorAll('span').forEach((span) => {
      const baseText = span.textContent.replace(/\s*\([\d,]+\)\s*$/, '').trim();
      span.textContent = `${baseText} (${count})`;
    });
  }

  function syncSupplementFacetCounts() {
    if (supplementProducts.length === 0) return;

    const facetsRoot = document.getElementById('main-search-filters');
    if (!facetsRoot) return;

    getInteractiveFacetCheckboxes(facetsRoot).forEach((input) => {
        const count = countProductsForFacetOption(input);
        const disabled = count === 0 && !input.checked;

        updateFacetOptionLabel(input, count);

        input.disabled = disabled;
        input
          .closest('.facet-checkbox, .mobile-facets__label')
          ?.classList.toggle('facet-checkbox--disabled', disabled);
        input
          .closest('.facet-checkbox, .mobile-facets__label')
          ?.classList.toggle('mobile-facets__label--disabled', disabled);

        const listItem = input.closest('li');
        if (listItem) {
          listItem.hidden = disabled;
          listItem.style.display = disabled ? 'none' : '';
        }
      });

    facetsRoot.querySelectorAll('.js-filter').forEach((filterGroup) => {
      const checkboxOptions = getInteractiveFacetCheckboxes(filterGroup);

      if (checkboxOptions.length === 0) return;

      const visibleOptions = checkboxOptions.filter(
        (input) => !input.disabled || input.checked,
      );

      filterGroup.hidden = visibleOptions.length === 0;
      filterGroup.style.display = visibleOptions.length === 0 ? 'none' : '';
    });
  }

  function applyClientFilters() {
    if (supplementProducts.length === 0) return;

    ensureSupplementProductsInGrid();

    const params = getFacetFormParams();
    const filtersActive = hasActiveFacetFilters(params);
    const matchingHandles = new Set(
      supplementProducts
        .filter((product) =>
          filtersActive ? productMatchesParams(product, params) : true,
        )
        .map((product) => product.handle),
    );

    document
      .querySelectorAll('#product-grid [data-product-handle]')
      .forEach((element) => {
        const item = element.closest('.grid__item') || element;
        const show = matchingHandles.has(element.dataset.productHandle);
        item.hidden = !show;
        item.style.display = show ? '' : 'none';
      });

    applyClientSort(params.get('sort_by'));
    updateSearchFacetsCount(getVisibleProductCount());
    syncFacetSelectionUi();
    syncSupplementFacetCounts();
    clearSupplementLoadingState();
  }

  function onSupplementFacetChange(event) {
    if (!supplementClientMode) return;

    const target = event.target;
    if (!target.closest('#main-search-filters')) return;

    if (target.closest('#FacetFiltersFormMobile')) {
      activeFacetFormId = 'FacetFiltersFormMobile';
    } else if (target.closest('#FacetFiltersForm')) {
      activeFacetFormId = 'FacetFiltersForm';
    }

    if (target.type === 'checkbox' && target.name?.startsWith('filter.')) {
      syncMatchingFacetCheckboxes(target, target.checked);
    }

    if (target.name?.startsWith('filter.v.price')) {
      syncMatchingPriceInputs(target);
    }

    if (target.name === 'sort_by') {
      syncSortSelects(target.value);
    }

    if (event.type === 'input' && target.type === 'range') {
      return;
    }

    applyClientFilters();
  }

  function onSupplementFacetClick(event) {
    if (!supplementClientMode) return;

    const removePill = event.target.closest('[data-supplement-facet-remove]');
    if (removePill) {
      event.preventDefault();
      event.stopPropagation();

      const filterName = removePill.dataset.filterName;
      const filterValue = removePill.dataset.filterValue;

      document
        .querySelectorAll(
          `input[type="checkbox"][name="${filterName}"][value="${filterValue}"]`,
        )
        .forEach((input) => {
          if (isInteractiveFacetCheckbox(input)) {
            input.checked = false;
          }
        });

      applyClientFilters();
      return;
    }

    const facetLink = event.target.closest(
      '.facets__reset, .active-facets__button-remove',
    );
    if (!facetLink) return;

    event.preventDefault();
    event.stopPropagation();

    if (facetLink.classList.contains('active-facets__button-remove')) {
      resetAllSupplementFacets();
      applyClientFilters();
      return;
    }

    const filterGroup = facetLink.closest('.js-filter');
    resetSupplementFacetGroup(filterGroup);

    setSupplementFacetQuery(supplementSearchQuery);
    applyClientFilters();
  }

  function installClientFilterInterceptor(root) {
    if (root.dataset.searchSupplementEventsBound === 'true') return;

    root.dataset.searchSupplementEventsBound = 'true';
    root.dataset.searchSupplementClientFilters = 'true';
    supplementClientMode = true;

    root.addEventListener('change', onSupplementFacetChange, true);
    root.addEventListener('input', onSupplementFacetChange, true);
    root.addEventListener('click', onSupplementFacetClick, true);
    document.addEventListener('search-supplement:apply-filters', applyClientFilters);
  }

  function dismissSupplementLoading(root) {
    root.removeAttribute('data-search-supplement-pending');
    document
      .querySelectorAll('[data-search-supplement-loading]')
      .forEach((element) => element.remove());
    clearSupplementLoadingState();
  }

  function parseSupplementProductItems(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    return [...doc.querySelectorAll('[data-supplement-product]')];
  }

  function finalizeSearchSupplement(root, addedCount) {
    dismissSupplementLoading(root);

    const nativeCount = Number(root.dataset.nativeResultsCount || 0);
    if (nativeCount > 0) return;

    const productCount = getVisibleProductCount();
    const noResults = root.querySelector('[data-search-no-results]');

    if (addedCount > 0 || productCount > 0) {
      noResults?.remove();
      root.classList.remove('template-search--empty');
      updateSearchFacetsCount(productCount);
      return;
    }

    updateSearchFacetsCount(0);

    if (noResults) {
      noResults.hidden = false;
    }
    root.classList.add('template-search--empty');
  }

  async function initSearchResultsSupplement() {
    const root = document.querySelector('[data-search-results-root]');
    if (!root) return;

    const nativeCount = Number(root.dataset.nativeResultsCount || 0);
    if (nativeCount > 0) return;

    const originalQuery = root.dataset.searchTerms?.trim();
    if (!originalQuery) {
      finalizeSearchSupplement(root, 0);
      return;
    }

    const grid = document.querySelector('#product-grid ul.product-grid');
    if (!grid) {
      finalizeSearchSupplement(root, 0);
      return;
    }

    const facetsAside = document.querySelector('[data-search-supplement-facets]');

    if (facetsAside) {
      root.dataset.searchSupplementClientFilters = 'true';
      supplementClientMode = true;
      installClientFilterInterceptor(root);
    }

    const facetsPromise = facetsAside
      ? loadCollectionFacetsShell(facetsAside, originalQuery, root)
      : Promise.resolve(false);

    const existingHandles = new Set(
      [...document.querySelectorAll('#product-grid [data-product-handle]')].map(
        (element) => element.dataset.productHandle,
      ),
    );

    const supplementUrl =
      root.dataset.searchSupplementUrl ||
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        originalQuery,
      )}&options%5Bprefix%5D=none&section_id=search-product-supplement&resources[type]=product&resources[limit]=20&resources[options][fields]=${encodeURIComponent(
        SEARCH_FIELDS,
      )}`;

    let addedCount = 0;

    try {
      const [response] = await Promise.all([
        fetch(supplementUrl),
        facetsPromise.catch(() => false),
      ]);

      if (!response.ok) throw new Error(response.status);

      const items = parseSupplementProductItems(await response.text());
      const supplementHandles = [];

      items.forEach((item) => {
        const handle = item.dataset.productHandle;
        if (!handle || existingHandles.has(handle)) return;

        supplementHandles.push({ handle, element: item });
      });

      const productData = (
        await Promise.all(
          supplementHandles.map(({ handle }) => fetchProduct(handle)),
        )
      ).filter(Boolean);

      let matchedProducts = productData;
      if (productData.length > 1) {
        const skuMatches = productData.filter((product) =>
          productMatchesSearchQuery(product, originalQuery),
        );
        if (skuMatches.length > 0) {
          matchedProducts = skuMatches;
        }
      }

      const matchedHandleSet = new Set(matchedProducts.map((p) => p.handle));

      supplementHandles.forEach(({ handle, element }) => {
        if (!matchedHandleSet.has(handle)) return;

        cacheSupplementProductElement(handle, element);
        grid.appendChild(element);
        existingHandles.add(handle);
        addedCount += 1;
      });

      if (facetsAside) {
        facetsAside.removeAttribute('data-search-supplement-facets');
      }

      if (addedCount === 0) {
        root.removeAttribute('data-search-supplement-client-filters');
        root.removeAttribute('data-search-supplement-events-bound');
        supplementClientMode = false;
        finalizeSearchSupplement(root, 0);
        return;
      }

      supplementProducts = matchedProducts;

      supplementSearchQuery = originalQuery;
      setSupplementFacetQuery(originalQuery);
      rememberPriceFilterDefaults();
      installClientFilterInterceptor(root);
      syncSupplementFacetCounts();
      applyClientFilters();
      finalizeSearchSupplement(root, addedCount);
    } catch {
      root.removeAttribute('data-search-supplement-client-filters');
      root.removeAttribute('data-search-supplement-events-bound');
      supplementClientMode = false;
      finalizeSearchSupplement(root, 0);
    }
  }

  initSearchResultsSupplement();
})();
